import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import hero_image from "../assests/hero_image.png";
import API_BASE_URL from "../config/api";

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(50px) translateX(-50%); }
  to { opacity: 1; transform: translateY(0) translateX(-50%); }
`;

// --- Styled Components ---

const HeroWrapper = styled.div`
  position: relative;
  height: 85vh; /* Immersive height */
  min-height: 600px;
  width: 100%;
  margin-bottom: 0; 
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Gradient Overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0, 78, 97, 0.3) 0%, rgba(0, 0, 0, 0.1) 100%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    height: 70vh;
    min-height: 500px;
  }
`;

const HeroImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: -1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center; 
  justify-content: center;
  height: 100%;
  padding-bottom: 80px; 
`;

const TextCard = styled.div`
  background: rgba(255, 255, 255, 0.6); /* Semi-transparent white */
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 2.5rem 4rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 32px rgba(0, 78, 97, 0.15); /* Subtle shadow using brand color */
  margin-bottom: 2rem;
  max-width: 900px;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    padding: 1.5rem 2rem;
    background: rgba(255, 255, 255, 0.75); /* Slightly more opaque on mobile for better readability */
  }
`;

const Title = styled.h1`
  font-family: "Merriweather", serif;
  font-size: clamp(32px, 5vw, 64px); 
  font-weight: 700;
  color: #004e61; /* Dark Teal for contrast */
  margin-bottom: 0.5rem;
  line-height: 1.2;

  span {
    color: #1CA8CB; /* Cyan Highlight */
  }
`;

const Subtitle = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: clamp(16px, 2vw, 18px); /* Slightly refined size */
  font-weight: 500; /* Increased weight */
  color: #004e61; /* Dark Teal */
  margin-top: 1rem;
  margin-bottom: 0;
  max-width: 100%;
`;

const SearchContainer = styled.div`
  position: absolute;
  bottom: -40px; 
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: flex-end; 
  gap: 1.5rem;
  
  /* Glassmorphism */
  background: rgba(255, 255, 255, 0.9); /* More opaque */
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  
  padding: 1.5rem 2.5rem;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 78, 97, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.8);
  width: 90%;
  max-width: 1100px;
  animation: ${slideUp} 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) 0.4s backwards;

  @media (max-width: 1024px) {
    width: 95%;
    gap: 1rem;
    padding: 1.5rem;
  }

  @media (max-width: 900px) {
    flex-wrap: wrap;
    bottom: -80px; 
    border-radius: 15px;
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch; 
    bottom: -150px;
    padding: 1.5rem;
    gap: 1.5rem;
  }
`;

const SearchField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 200px;

  @media (max-width: 600px) {
    width: 100%;
    min-width: auto;
  }
`;

const FieldLabel = styled.label`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold label */
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  color: #004e61;
  text-transform: uppercase;
  text-align: left;
`;

// Common style for inputs
const inputStyles = css`
  width: 100%;
  padding: 12px 15px;
  border-radius: 12px;
  border: 1px solid rgba(0, 78, 97, 0.2);
  background: #f9fbfb; /* Very light subtle background */
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  color: #004e61;
  font-weight: 500;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: #1CA8CB;
    box-shadow: 0 0 0 3px rgba(28, 168, 203, 0.1);
    background: white;
  }
`;

const StyledSelectInput = styled.select`
  ${inputStyles}
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23004e61' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  cursor: pointer;
`;

const SearchButton = styled.button`
  font-family: "Poppins", sans-serif;
  padding: 14px 32px;
  background: linear-gradient(135deg, #1ca8cb 0%, #004e61 100%);
  border: none;
  border-radius: 12px; 
  color: white;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(28, 168, 203, 0.3);
  margin-bottom: 2px; 

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(28, 168, 203, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    width: 100%;
    margin-top: 10px;
  }
`;

const CTAButton = styled.button`
  font-family: "Poppins", sans-serif;
  padding: 16px 40px;
  background: linear-gradient(135deg, #1ca8cb 0%, #004e61 100%);
  border: none;
  border-radius: 12px;
  color: white;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(28, 168, 203, 0.3);
  margin-top: 1.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(28, 168, 203, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 14px 32px;
    font-size: 16px;
    width: 100%;
  }
`;

const MicroText = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  color: #004e61;
  opacity: 0.7;
  margin-top: 10px;
  font-weight: 500;
`;

// React Select Custom Styles to match StyledSelectInput
const customSelectStyles = {
  container: (base) => ({
    ...base,
    width: "100%",
  }),
  control: (base, state) => ({
    ...base,
    borderRadius: "12px",
    padding: "2px",
    border: state.isFocused ? "1px solid #1CA8CB" : "1px solid rgba(0, 78, 97, 0.2)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(28, 168, 203, 0.1)" : "none",
    "&:hover": {
      borderColor: "#1CA8CB",
    },
    backgroundColor: "#f9fbfb",
    fontFamily: '"Poppins", sans-serif',
    fontSize: "16px",
    color: "#004e61",
    fontWeight: "500",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#004e61",
    fontWeight: "400",
    opacity: 0.3,
  }),
  singleValue: (base) => ({
    ...base,
    color: "#004e61",
    fontWeight: "500",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    marginTop: "8px",
    boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
    zIndex: 100,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#1ca8cb"
      : state.isFocused
        ? "#e9f6f9"
        : null,
    color: state.isSelected ? "white" : "#004e61",
    fontFamily: '"Poppins", sans-serif',
  }),
};

const Carousel = ({ onOpenModal }) => {
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/destinations`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setDestinations(data);
      } catch (error) {
        console.error("Error fetching destinations:", error);
      }
    };

    fetchDestinations();
  }, []);

  const handleSearch = () => {
    if (selectedDestination) {
      navigate(`/DestinationPackages?country=${selectedDestination.value}`);
    } else {
      alert("Please select a destination");
    }
  };

  const destinationOptions = destinations.map((destination) => ({
    value: destination.name,
    label: destination.name,
  }));

  return (
    <HeroWrapper>
      <HeroImage src={hero_image} alt="Explore the world" />

      <ContentWrapper>
        <TextCard>
          {/* Original hero text - commented out
          <Title>Plan Like a Pro with <span>AI</span></Title>
          <Subtitle>Your personalized holiday adventure starts here. Discover the world your way.</Subtitle>
          */}
          <Title>Plan your Kerala honeymoon in <span>5 minutes</span></Title>
          <Subtitle>Get a complete itinerary with hotels, route, and full cost, no agents, no confusion</Subtitle>
          <CTAButton onClick={() => {
            // Track "Start Planning Free" click
            import("react-ga4").then((ReactGA) => {
              ReactGA.default.event({
                category: "Planner",
                action: "Start Planning",
                label: "Hero CTA",
              });
            });
            onOpenModal();
          }}>👉 Check Your Trip Cost</CTAButton>
          <MicroText>No signup required • Fully customizable</MicroText>
        </TextCard>
      </ContentWrapper>

      {/* Search Container - commented out
      <SearchContainer>
        <SearchField>
          <FieldLabel htmlFor="whereTo">Where to?</FieldLabel>
          <Select
            id="whereTo"
            options={destinationOptions}
            placeholder="Select Destination"
            styles={customSelectStyles}
            onChange={setSelectedDestination}
          />
        </SearchField>

        <SearchField>
          <FieldLabel htmlFor="month">Month</FieldLabel>
          <StyledSelectInput id="month">
            <option>Select Month</option>
            {[
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December",
            ].map((month) => (
              <option key={month}>{month}</option>
            ))}
          </StyledSelectInput>
        </SearchField>

        <SearchField>
          <FieldLabel htmlFor="travelDuration">Duration</FieldLabel>
          <StyledSelectInput id="travelDuration">
            <option>Select Duration</option>
            {[...Array(10).keys()].map((i) => (
              <option key={i} value={i + 1}>
                {i + 1} Day{i + 1 > 1 ? "s" : ""}
              </option>
            ))}
            <option value="above">10+ Days</option>
          </StyledSelectInput>
        </SearchField>

        <SearchButton onClick={handleSearch}>Search</SearchButton>
      </SearchContainer>
      */}
    </HeroWrapper>
  );
};

export default Carousel;